import React, { useEffect, useMemo, useState, useRef, useLayoutEffect } from "react";
import {
  Box, Flex, Heading, Spinner, Tabs, TabList, TabPanels, Tab, TabPanel,
  Button, useToast, Text, VStack, HStack, Badge, useDisclosure, Modal,
  ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
  ModalFooter, FormControl, FormLabel, Input, Textarea, IconButton, Select,
  SimpleGrid, ButtonGroup, Tooltip, Divider, Tag, TagLabel, Drawer,
  DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter
} from "@chakra-ui/react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Plus, Trash2, LayoutGrid, Rows3, Minimize2, Pencil } from "lucide-react";
import { useCognito } from "../utils/useCognito";
import {
  getBoards, getLists, getTasksForList, createTask, updateTask, deleteTask, createList, createBoard
} from "../lib/kanbanClient";

// ---------- Presentational bits ----------
const COL_W = 340; // keep in sync with Column
const GAP_PX = 16; // Chakra spacing 4

const Column = ({ title, children }) => (
  <Box
    w={`${COL_W}px`}
    minW={`${COL_W}px`}
    bg="gray.50"
    _dark={{ bg: "gray.800" }}
    rounded="2xl"
    p={3}
    mr={4}
    boxShadow="sm"
  >
    <HStack justify="space-between" mb={2}>
      <Heading size="sm" noOfLines={2} wordBreak="break-word" maxW="100%">{title}</Heading>
    </HStack>
    {children}
  </Box>
);

const priorityColor = (p) => {
  switch ((p || "").toLowerCase()) {
    case "urgent": return "red";
    case "high": return "orange";
    case "medium": return "yellow";
    case "low": return "green";
    default: return "gray";
  }
};

const TaskCard = ({ task, onDelete, onOpenView }) => (
  <Box bg="white" _dark={{ bg: "gray.700" }} rounded="xl" p={3} mb={3} boxShadow="xs">
    <VStack align="start" spacing={2}>
      <Button
        variant="link"
        color="blue.500"
        onClick={() => onOpenView(task)}
        fontWeight="semibold"
        px={0}
        _hover={{ transform: "scale(1)" }}
        textAlign="left"
        whiteSpace="normal"
        wordBreak="break-word"
        minH="auto"
        h="auto"
        maxW="100%"
      >
        {task.title}
      </Button>
      <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }} noOfLines={3}>
        {task.description}
      </Text>
      <HStack w="100%" justify="space-between">
        <HStack>
          {task.priority && <Badge colorScheme={priorityColor(task.priority)} textTransform="capitalize">{task.priority}</Badge>}
          {task.dueDate && <Badge>{task.dueDate.split("T")[0]}</Badge>}
          {typeof task.timeEstimateMins === "number" && (
            <Tag size="sm" variant="subtle" colorScheme="blue">
              <TagLabel>{task.timeEstimateMins}m</TagLabel>
            </Tag>
          )}
        </HStack>
        <IconButton
          aria-label="Delete task"
          size="xs"
          icon={<Trash2 size={14} />}
          onClick={onDelete}
          _hover={{ transform: "scale(1)" }}
        />
      </HStack>
    </VStack>
  </Box>
);

// ---------- Modals / Drawers ----------
const NewTaskModal = ({ isOpen, onClose, onCreate, defaultListId }) => {
  const [form, setForm] = useState({ title: "", description: "", dueDate: "", timeEstimateMins: "" });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) return;
    setSaving(true);
    try {
      await onCreate({
        title: form.title.trim(),
        description: form.description.trim(),
        dueDate: form.dueDate || undefined,
        timeEstimateMins: form.timeEstimateMins ? Number(form.timeEstimateMins) : undefined,
        listId: defaultListId,
      });
      onClose();
      setForm({ title: "", description: "", dueDate: "", timeEstimateMins: "" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>New Task</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Description</FormLabel>
              <Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </FormControl>
            <HStack>
              <FormControl>
                <FormLabel>Due date</FormLabel>
                <Input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Time estimate (mins)</FormLabel>
                <Input type="number" min="0" value={form.timeEstimateMins} onChange={e => setForm({ ...form, timeEstimateMins: e.target.value })} />
              </FormControl>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button mr={3} onClick={onClose} variant="ghost" _hover={{ transform: "scale(1)" }}>Cancel</Button>
          <Button colorScheme="blue" isLoading={saving} onClick={submit} _hover={{ transform: "scale(1)" }}>
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const TaskDetailModal = ({ isOpen, onClose, task, onSave, onDelete }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (task) {
      setForm({
        title: task.title || "",
        description: task.description || "",
        dueDate: task.dueDate ? task.dueDate.split("T")[0] : "",
        timeEstimateMins: task.timeEstimateMins ?? "",
        priority: task.priority || "",
      });
    } else {
      setForm(null);
    }
  }, [task]);

  if (!task || !form) return null;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(task.taskId, {
        title: form.title.trim(),
        description: form.description.trim(),
        dueDate: form.dueDate ? `${form.dueDate}T00:00:00.000Z` : null,
        timeEstimateMins: form.timeEstimateMins === "" ? null : Number(form.timeEstimateMins),
        priority: form.priority || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Edit Task</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Title</FormLabel>
              <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Description</FormLabel>
              <Textarea rows={6} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
            </FormControl>
            <HStack>
              <FormControl>
                <FormLabel>Due date</FormLabel>
                <Input type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Time estimate (mins)</FormLabel>
                <Input type="number" min="0" value={form.timeEstimateMins} onChange={e => setForm({ ...form, timeEstimateMins: e.target.value })} />
              </FormControl>
            </HStack>
            <FormControl>
              <FormLabel>Priority</FormLabel>
              <Select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                <option value="">None</option>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="urgent">urgent</option>
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <HStack w="100%" justify="space-between">
            <Button variant="outline" colorScheme="red" onClick={() => onDelete(task)} _hover={{ transform: "scale(1)" }}>
              Delete
            </Button>
            <HStack>
              <Button onClick={onClose} variant="ghost" _hover={{ transform: "scale(1)" }}>Cancel</Button>
              <Button colorScheme="blue" isLoading={saving} onClick={save} _hover={{ transform: "scale(1)" }}>
                Save
              </Button>
            </HStack>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

/** Read-only details view — launched by clicking the task title */
const TaskViewDrawer = ({ isOpen, onClose, task, onEdit }) => {
  if (!task) return null;

  const due = task.dueDate ? task.dueDate.split("T")[0] : null;
  const prColor = priorityColor(task.priority);
  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerHeader>
          <HStack justify="space-between" align="start">
            <VStack align="start" spacing={1} flex="1" minW={0}>
              <Heading size="md" noOfLines={3} wordBreak="break-word">{task.title}</Heading>
              <HStack spacing={2}>
                {task.priority && <Badge colorScheme={prColor} textTransform="capitalize">{task.priority}</Badge>}
                {due && <Badge>{due}</Badge>}
                {typeof task.timeEstimateMins === "number" && (
                  <Tag size="sm" variant="subtle" colorScheme="blue">
                    <TagLabel>{task.timeEstimateMins}m</TagLabel>
                  </Tag>
                )}
              </HStack>
            </VStack>
            <Button
              leftIcon={<Pencil size={16} />}
              onClick={() => onEdit(task)}
              colorScheme="blue"
              _hover={{ transform: "scale(1)" }}
            >
              Edit
            </Button>
          </HStack>
        </DrawerHeader>
        <DrawerBody>
          <VStack align="stretch" spacing={4}>
            <Box>
              <Text fontSize="xs" color="gray.500" mb={1}>Description</Text>
              <Box
                bg="gray.50"
                _dark={{ bg: "gray.800" }}
                borderWidth="1px"
                borderColor="gray.100"
                rounded="lg"
                p={3}
              >
                <Text whiteSpace="pre-wrap">{task.description || "—"}</Text>
              </Box>
            </Box>

            <Divider />

            <SimpleGrid columns={2} spacing={3}>
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>Priority</Text>
                <Badge colorScheme={prColor} textTransform="capitalize">
                  {task.priority || "none"}
                </Badge>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>Due Date</Text>
                <Text>{due || "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>Time Estimate</Text>
                <Text>{typeof task.timeEstimateMins === "number" ? `${task.timeEstimateMins} minutes` : "—"}</Text>
              </Box>
              <Box>
                <Text fontSize="xs" color="gray.500" mb={1}>Task ID</Text>
                <Text fontFamily="mono" fontSize="sm">{task.taskId}</Text>
              </Box>
            </SimpleGrid>
          </VStack>
        </DrawerBody>
        <DrawerFooter>
          <HStack w="100%" justify="space-between">
            <Button onClick={onClose} variant="ghost" _hover={{ transform: "scale(1)" }}>Close</Button>
            <Button
              leftIcon={<Pencil size={16} />}
              onClick={() => onEdit(task)}
              colorScheme="blue"
              _hover={{ transform: "scale(1)" }}
            >
              Edit
            </Button>
          </HStack>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

// ---------- Page ----------
const AdminKanban = () => {
  const { getIdToken } = useCognito();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [boards, setBoards] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [lists, setLists] = useState([]);
  const [tasksByList, setTasksByList] = useState({});

  const [creatingForList, setCreatingForList] = useState(null);
  const newTaskModal = useDisclosure();

  const [detailTask, setDetailTask] = useState(null);
  const detailModal = useDisclosure();

  // NEW: view-only drawer state
  const [viewTask, setViewTask] = useState(null);
  const viewDrawer = useDisclosure();

  // View mode ('scroll' | 'grid' | 'fit') with localStorage persistence
  const [viewMode, setViewMode] = useState(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem("kanban:viewMode") : null;
    return stored === "grid" || stored === "fit" ? stored : "scroll";
  });
  useEffect(() => {
    try { window.localStorage.setItem("kanban:viewMode", viewMode); } catch {}
  }, [viewMode]);

  const activeBoard = useMemo(() => boards[activeIndex], [boards, activeIndex]);

  // initial load: boards
  useEffect(() => {
    (async () => {
      try {
        const bs = await getBoards(getIdToken);
        const bsArr = Array.isArray(bs) ? bs : (bs && Array.isArray(bs.boards) ? bs.boards : []);
        const ordered = [...bsArr].sort((a, b) => {
          const av = a.boardType === "dev" ? 0 : 1;
          const bv = b.boardType === "dev" ? 0 : 1;
          return av - bv;
        });
        setBoards(ordered);
        if (ordered.length === 0) {
          toast({ title: "No boards found", description: "Use the button below to create Dev/Ops boards.", status: "warning" });
        }
      } catch (e) {
        console.error(e);
        toast({ title: "Failed to load boards", status: "error" });
      } finally {
        setLoading(false);
      }
    })();
  }, [getIdToken, toast]);

  // whenever active board changes, load lists + tasks
  useEffect(() => {
    if (!activeBoard?.boardId) return;
    (async () => {
      try {
        const ls = await getLists(getIdToken, activeBoard.boardId);
        setLists(ls);
        const entries = await Promise.all(
          ls.map(async (l) => [l.listId, await getTasksForList(getIdToken, { boardId: activeBoard.boardId, listId: l.listId })])
        );
        setTasksByList(Object.fromEntries(entries));
      } catch (e) {
        console.error(e);
        toast({ title: "Failed to load board", status: "error" });
      }
    })();
  }, [activeBoard?.boardId, getIdToken, toast]);

  // DnD
  const onDragEnd = async (result) => {
    if (viewMode === "fit") return; // drag disabled in fit mode due to transform math
    const { destination, source, draggableId } = result;
    if (!destination) return;

    const fromListId = source.droppableId;
    const toListId = destination.droppableId;
    const fromIndex = source.index;
    const toIndex = destination.index;

    if (fromListId === toListId && fromIndex === toIndex) return;

    const fromArr = [...(tasksByList[fromListId] || [])];
    const toArr = fromListId === toListId ? fromArr : [...(tasksByList[toListId] || [])];

    const body = { toListId };
    if (toArr.length === 0) {
      // append to end server-side
    } else if (toIndex === 0) {
      body.newOrderBeforeTaskId = toArr[0].taskId;
    } else {
      const prev = toArr[toIndex - 1];
      if (fromListId === toListId && toIndex > fromIndex) {
        const prevAdjusted = toArr[toIndex];
        if (prevAdjusted) body.newOrderAfterTaskId = prevAdjusted.taskId;
      } else {
        if (prev) body.newOrderAfterTaskId = prev.taskId;
      }
    }

    // optimistic UI
    const moving = fromArr[fromIndex];
    const next = { ...tasksByList };
    next[fromListId] = [...(tasksByList[fromListId] || [])];
    next[fromListId].splice(fromIndex, 1);
    if (fromListId === toListId) {
      next[toListId].splice(toIndex, 0, moving);
    } else {
      next[toListId] = [...(tasksByList[toListId] || [])];
      next[toListId].splice(toIndex, 0, { ...moving, listId: toListId });
    }
    setTasksByList(next);

    try {
      const updated = await updateTask(getIdToken, draggableId, body);
      setTasksByList((prev) => {
        const clone = { ...prev };
        clone[fromListId] = (clone[fromListId] || []).filter((t) => t.taskId !== draggableId);
        clone[toListId] = (clone[toListId] || []).map((t) => (t.taskId === draggableId ? updated : t));
        return clone;
      });
    } catch (e) {
      console.error(e);
      toast({ title: "Move failed — reverting", status: "error" });
      const ls = await getLists(getIdToken, activeBoard.boardId);
      const entries = await Promise.all(
        ls.map(async (l) => [l.listId, await getTasksForList(getIdToken, { boardId: activeBoard.boardId, listId: l.listId })])
      );
      setLists(ls);
      setTasksByList(Object.fromEntries(entries));
    }
  };

  const addTask = async ({ title, description, dueDate, timeEstimateMins, listId }) => {
    try {
      const t = await createTask(getIdToken, {
        boardId: activeBoard.boardId,
        listId,
        title, description,
        dueDate: dueDate ? `${dueDate}T00:00:00.000Z` : undefined,
        timeEstimateMins
      });
      setTasksByList((prev) => ({ ...prev, [listId]: [...(prev[listId] || []), t] }));
      toast({ title: "Task created", status: "success" });
    } catch (e) {
      console.error(e);
      toast({ title: "Create failed", status: "error" });
    }
  };

  const saveTask = async (taskId, patch) => {
    try {
      const updated = await updateTask(getIdToken, taskId, patch);
      setTasksByList((prev) => {
        const clone = { ...prev };
        const lid = updated.listId;
        clone[lid] = (clone[lid] || []).map((t) => (t.taskId === updated.taskId ? updated : t));
        return clone;
      });
      toast({ title: "Task updated", status: "success" });
    } catch (e) {
      console.error(e);
      toast({ title: "Update failed", status: "error" });
    }
  };

  const removeTask = async (task) => {
    try {
      await deleteTask(getIdToken, task.taskId);
      setTasksByList((prev) => ({ ...prev, [task.listId]: (prev[task.listId] || []).filter((t) => t.taskId !== task.taskId) }));
    } catch (e) {
      console.error(e);
      toast({ title: "Delete failed", status: "error" });
    }
  };

  const addList = async () => {
    try {
      const last = lists[lists.length - 1];
      const newList = await createList(getIdToken, activeBoard.boardId, {
        listName: `List ${lists.length + 1}`,
        afterListId: last?.listId
      });
      setLists((prev) => [...prev, newList]);
      setTasksByList((prev) => ({ ...prev, [newList.listId]: [] }));
    } catch (e) {
      console.error(e);
      toast({ title: "Failed to create column", status: "error" });
    }
  };

  // ---------- Fit view measurements ----------
  const fitOuterRef = useRef(null);
  const fitInnerRef = useRef(null);
  const [fitScale, setFitScale] = useState(1);
  const [fitHeight, setFitHeight] = useState(null);

  useLayoutEffect(() => {
    if (viewMode !== "fit") return;

    const calc = () => {
        const outer = fitOuterRef.current;
        const inner = fitInnerRef.current;
        if (!outer || !inner) return;

        const containerWidth = outer.clientWidth || 1;
        const totalWidth = inner.scrollWidth || 1; // natural width of the row
        const s = Math.min(1, containerWidth / totalWidth); // never upscale
        setFitScale(s);

        // inner.offsetHeight is unscaled; multiply by s to reserve space
        const h = inner.offsetHeight * s;
        setFitHeight(h + 16);
    };

    // Run once after mount/paint
    queueMicrotask(calc);

    const ro = new ResizeObserver(() => calc());

    // Only observe if refs are attached
    const outerEl = fitOuterRef.current;
    const innerEl = fitInnerRef.current;
    if (outerEl) ro.observe(outerEl);
    if (innerEl) ro.observe(innerEl);

    // Also recalc on window resizes (helps with zoom / scrollbars)
    window.addEventListener("resize", calc);

    return () => {
        try { ro.disconnect(); } catch {}
        window.removeEventListener("resize", calc);
    };
    }, [viewMode, lists, tasksByList]);

  if (loading) {
    return (
      <Flex minH="70vh" align="center" justify="center"><Spinner size="xl" /></Flex>
    );
  }

  if (!activeBoard) {
    const createDefaults = async () => {
      try {
        await createBoard(getIdToken, { name: "Dev Work", type: "dev" });
        await createBoard(getIdToken, { name: "Ops Work", type: "ops" });
        toast({ title: "Boards created", status: "success" });
        const bs = await getBoards(getIdToken);
        const bsArr = Array.isArray(bs) ? bs : (bs && Array.isArray(bs.boards) ? bs.boards : []);
        setBoards(bsArr);
        setActiveIndex(0);
      } catch (e) {
        console.error(e);
        toast({ title: "Failed to create boards", status: "error" });
      }
    };

    return (
      <Box p={6}>
        <Heading size="md" mb={4}>No boards yet</Heading>
        <Button colorScheme="blue" onClick={createDefaults} _hover={{ transform: "scale(1)" }}>
          Create Dev &amp; Ops Boards
        </Button>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={4}>
        <Heading size="lg">Kanban</Heading>
        <HStack spacing={3}>
          <NewListButton onCreate={addList} />
          <ButtonGroup isAttached variant="outline">
            <Tooltip label="Row mode (scroll sideways)" hasArrow>
              <Button
                leftIcon={<Rows3 size={16} />}
                colorScheme={viewMode === "scroll" ? "blue" : undefined}
                onClick={() => setViewMode("scroll")}
                _hover={{ transform: "scale(1)" }}
                aria-pressed={viewMode === "scroll"}
              >
                Row
              </Button>
            </Tooltip>
            <Tooltip label="Grid mode (wrap to next row)" hasArrow>
              <Button
                leftIcon={<LayoutGrid size={16} />}
                colorScheme={viewMode === "grid" ? "blue" : undefined}
                onClick={() => setViewMode("grid")}
                _hover={{ transform: "scale(1)" }}
                aria-pressed={viewMode === "grid"}
              >
                Grid
              </Button>
            </Tooltip>
            <Tooltip label="Fit mode (single row, scaled to fit — drag disabled)" hasArrow>
              <Button
                leftIcon={<Minimize2 size={16} />}
                colorScheme={viewMode === "fit" ? "blue" : undefined}
                onClick={() => setViewMode("fit")}
                _hover={{ transform: "scale(1)" }}
                aria-pressed={viewMode === "fit"}
              >
                Fit
              </Button>
            </Tooltip>
          </ButtonGroup>
        </HStack>
      </HStack>

      <Tabs index={activeIndex} onChange={setActiveIndex} variant="enclosed" mb={4}>
        <TabList>
          {boards.map((b) => (
            <Tab key={b.boardId}>{b.boardName} <Badge ml={2}>{b.boardType}</Badge></Tab>
          ))}
        </TabList>
        <TabPanels>
          {boards.map((b) => <TabPanel key={b.boardId} p={0} />)}
        </TabPanels>
      </Tabs>

      {/* Views */}
      <DragDropContext onDragEnd={onDragEnd}>
        {viewMode === "scroll" && (
          <Flex overflowX="auto" pb={6}>
            {lists.map((list) => (
              <Droppable droppableId={list.listId} key={list.listId} ignoreContainerClipping>
                {(provided) => (
                  <Column title={list.listName}>
                    <VStack align="stretch" ref={provided.innerRef} {...provided.droppableProps}>
                      {(tasksByList[list.listId] || []).map((task, index) => (
                        <Draggable draggableId={task.taskId} index={index} key={task.taskId}>
                          {(prov) => (
                            <Box ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}>
                              <TaskCard
                                task={task}
                                onDelete={() => removeTask(task)}
                                onOpenView={(t) => { setViewTask(t); viewDrawer.onOpen(); }}
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </VStack>

                    <Button
                      mt={3}
                      w="100%"
                      leftIcon={<Plus size={16} />}
                      onClick={() => { setCreatingForList(list.listId); newTaskModal.onOpen(); }}
                      _hover={{ transform: "scale(1)" }}
                    >
                      Add Task
                    </Button>
                  </Column>
                )}
              </Droppable>
            ))}
          </Flex>
        )}

        {viewMode === "grid" && (
          <SimpleGrid minChildWidth={`${COL_W}px`} spacing={4} pb={6}>
            {lists.map((list) => (
              <Droppable droppableId={list.listId} key={list.listId} ignoreContainerClipping>
                {(provided) => (
                  <Box
                    bg="gray.50"
                    _dark={{ bg: "gray.800" }}
                    rounded="2xl"
                    p={3}
                    boxShadow="sm"
                    minW={`${COL_W}px`}
                  >
                    <HStack justify="space-between" mb={2}>
                      <Heading size="sm" noOfLines={2} wordBreak="break-word" maxW="100%">{list.listName}</Heading>
                    </HStack>

                    <VStack align="stretch" ref={provided.innerRef} {...provided.droppableProps}>
                      {(tasksByList[list.listId] || []).map((task, index) => (
                        <Draggable draggableId={task.taskId} index={index} key={task.taskId}>
                          {(prov) => (
                            <Box ref={prov.innerRef} {...prov.draggableProps} {...prov.dragHandleProps}>
                              <TaskCard
                                task={task}
                                onDelete={() => removeTask(task)}
                                onOpenView={(t) => { setViewTask(t); viewDrawer.onOpen(); }}
                              />
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </VStack>

                    <Button
                      mt={3}
                      w="100%"
                      leftIcon={<Plus size={16} />}
                      onClick={() => { setCreatingForList(list.listId); newTaskModal.onOpen(); }}
                      _hover={{ transform: "scale(1)" }}
                    >
                      Add Task
                    </Button>
                  </Box>
                )}
              </Droppable>
            ))}
          </SimpleGrid>
        )}

        {viewMode === "fit" && (
          <Box
            ref={fitOuterRef}
            overflow="hidden"
            pb={4}
            // reserve scaled height to avoid content cut-off
            style={{ height: fitHeight != null ? `${fitHeight}px` : undefined }}
          >
            <Box
              ref={fitInnerRef}
              // keep natural width; scale to fit in outer
              style={{
                transform: `scale(${fitScale})`,
                transformOrigin: "0 0",
                display: "inline-block"
              }}
            >
              <Flex>
                {lists.map((list, idx) => (
                  <Box key={list.listId} mr={idx === lists.length - 1 ? 0 : `${GAP_PX}px`}>
                    {/* Droppable still renders to keep DOM identical, but items are not draggable */}
                    <Droppable droppableId={list.listId} ignoreContainerClipping>
                      {(provided) => (
                        <Column title={list.listName}>
                          <VStack align="stretch" ref={provided.innerRef} {...provided.droppableProps}>
                            {(tasksByList[list.listId] || []).map((task) => (
                              // Draggable with drag disabled to prevent DnD math under transform
                              <Draggable
                                draggableId={task.taskId}
                                index={0}
                                key={task.taskId}
                                isDragDisabled
                              >
                                {(prov) => (
                                  <Box ref={prov.innerRef}>
                                    <TaskCard
                                      task={task}
                                      onDelete={() => removeTask(task)}
                                      onOpenView={(t) => { setViewTask(t); viewDrawer.onOpen(); }}
                                    />
                                  </Box>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </VStack>

                          <Button
                            mt={3}
                            w="100%"
                            leftIcon={<Plus size={16} />}
                            onClick={() => { setCreatingForList(list.listId); newTaskModal.onOpen(); }}
                            _hover={{ transform: "scale(1)" }}
                          >
                            Add Task
                          </Button>
                        </Column>
                      )}
                    </Droppable>
                  </Box>
                ))}
              </Flex>
            </Box>
            <Text mt={2} fontSize="xs" color="gray.500">
              Fit view: drag disabled (switch to Row or Grid to drag)
            </Text>
          </Box>
        )}
      </DragDropContext>

      {/* Modals */}
      <NewTaskModal
        isOpen={newTaskModal.isOpen}
        onClose={newTaskModal.onClose}
        onCreate={(payload) => addTask({ ...payload, listId: creatingForList })}
        defaultListId={creatingForList}
      />

      <TaskDetailModal
        isOpen={detailModal.isOpen}
        onClose={() => { detailModal.onClose(); setDetailTask(null); }}
        task={detailTask}
        onSave={saveTask}
        onDelete={removeTask}
      />

      {/* View-only Drawer */}
      <TaskViewDrawer
        isOpen={viewDrawer.isOpen}
        onClose={() => { viewDrawer.onClose(); setViewTask(null); }}
        task={viewTask}
        onEdit={(t) => {
          // open edit modal prefilled
          setDetailTask(t);
          viewDrawer.onClose();
          detailModal.onOpen();
        }}
      />
    </Box>
  );
};

const NewListButton = ({ onCreate }) => {
  const [creating, setCreating] = useState(false);
  return (
    <Button
      size="sm"
      leftIcon={<Plus size={16} />}
      isLoading={creating}
      onClick={async () => {
        setCreating(true);
        try { await onCreate(); } finally { setCreating(false); }
      }}
      _hover={{ transform: "scale(1)" }}
    >
      Add Column
    </Button>
  );
};

export default AdminKanban;
